import { useState, type FormEvent } from "react";
import { useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import Link from "@/lib/next-shim";
import { Button, ErrorText, Field, Input, PasswordInput } from "@/components/ui";
import { getDict } from "@/lib/i18n";
import { go } from "@/lib/nav";
import { loginEmail, setRemember, supabase } from "@/lib/supabase";

export function LoginPage() {
  const t = getDict("vi");
  const queryClient = useQueryClient();
  const search = useSearch({ strict: false }) as { next?: string };
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const username = String(form.get("username") ?? "").trim();
    const password = String(form.get("password") ?? "");
    if (!username || !password) return setError(true);
    setBusy(true);
    setRemember(form.get("remember") === "on");
    const { error } = await supabase().auth.signInWithPassword({ email: loginEmail(username), password });
    setBusy(false);
    if (error) return setError(true);
    queryClient.clear();
    // Only same-app paths are followed after login.
    const next = search.next?.startsWith("/") && !search.next.startsWith("//") ? search.next : "/";
    go(next);
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center px-6">
      <div className="mb-10 text-center">
        <img src="/icon.svg" alt="" className="mx-auto mb-4 h-16 w-16" />
        <h1 className="text-3xl font-bold">{t.appName}</h1>
        <p className="mt-2 text-sm font-semibold uppercase tracking-widest text-primary">{t.appSlogan}</p>
        <p className="mt-1 text-xs italic text-muted">{t.appSloganFull}</p>
      </div>
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label={t.username}>
          <Input name="username" autoComplete="username" autoCapitalize="none" required />
        </Field>
        <Field label={t.password}>
          <PasswordInput
            name="password"
            autoComplete="current-password"
            required
            showLabel={t.showPassword}
            hideLabel={t.hidePassword}
          />
        </Field>
        <div className="flex items-center justify-between gap-3 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" name="remember" defaultChecked className="h-4 w-4 accent-primary" />
            {t.rememberMe}
          </label>
          <Link href="/forgot-password" className="text-muted hover:text-text">
            {t.forgotPassword}
          </Link>
        </div>
        <ErrorText>{error ? t.invalidLogin : null}</ErrorText>
        <Button type="submit" className="w-full" disabled={busy}>
          {t.login}
        </Button>
      </form>
    </main>
  );
}
